import {
  Component,
  Input,
  ViewChild,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { finalize } from 'rxjs';
import { TruckSearch } from '@app/trucks/models/truck-search.model';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AppService } from '@app/app.service';
import { DatePipe } from '@angular/common';
import { LoadViewPublicDialogComponent } from '../load-view-public-dialog/load-view-public-dialog.component';
import { AuthService } from '@app/auth/auth.service';
import { CurrencyPipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-saved-loads',
  imports: [
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatIconModule,
    DatePipe,
    CurrencyPipe,
    MatTooltipModule,
    CommonModule
],
  templateUrl: './saved-loads.component.html',
  styleUrl: './saved-loads.component.scss',
})

export class SavedLoadsComponent implements OnInit, AfterViewInit {
  @Input({ required: true }) search!: TruckSearch;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading = false;

  results = new MatTableDataSource<any>([]);

  isMobile = window.innerWidth < 768;
  itemsPerPage = this.isMobile ? 10 : 25;

  desktopColumns = [
    'is_saved',
    'age',
    'pickup_dates',
    'rate',
    'rpm',
    'origin_city',
    'origin_state',
    'destination_city',
    'destination_state',
    'weight',
    'dimensions',
    'well_required',
  ];

  mobileColumns = [
    'mobile'
  ];

  displayedColumns = this.isMobile ? this.mobileColumns : this.desktopColumns;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
    this.displayedColumns = this.isMobile
      ? this.mobileColumns
      : this.desktopColumns;
  }

  constructor(
    private dialog: MatDialog,
    private appService: AppService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    if (!this.authService.userCanSearchLoads()) {
      this.appService.navigateToDefaultUrl();
      return;
    }

    this.getSavedLoads();
  }

  ngAfterViewInit(): void {
    this.results.paginator = this.paginator;
    this.results.sort = this.sort;

    this.results.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'age':
          return new Date(item.refreshed_at).getTime();
        
        case 'origin_deadhead_distance':
          return item.origin_deadhead_distance ?? 999999;

        case 'destination_deadhead_distance':
          return item.destination_deadhead_distance ?? 999999;

        case 'origin':
          return item.origin_city ?? '';

        case 'destination':
          return item.destination_city ?? '';

        case 'pickup_dates':
          return item.pickup_date_start ?? '';

        default:
          return item[property];
      }
    };
  }

  getSavedLoads(successMessage: boolean = false): void {
    this.loading = true;

    this.appService
      .get('saved/loads')
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.results.data = res.data ?? [];
          if (successMessage) {
            if (res.data.length === 0) {
              this.appService.warningSnackBar(res.message);
            } else {
              this.appService.successSnackBar(res.message);
            }
          }
        },
        error: (err) => {
          this.appService.showSnackBar(err.error.message, 'error');
        },
      });
  }

  viewLoadDetails(load: any): void {

    if (load.deleted_at || !load.visible) {
      this.appService.showSnackBar('This load is no longer available.', 'warning');
      return;
    }

    this.dialog.open(LoadViewPublicDialogComponent, {
      width: '900px',
      maxWidth: '900px',
      data: { 
        load: load, 
        toggleSavedLoad: () => this.toggleSavedLoad(load) 
      },
      
    });
  }

  getDimensions(load: any): string {
    const length = this.toFeetAndInches(load.length);
    const width = this.toFeetAndInches(load.width);
    const height = this.toFeetAndInches(load.height);

    const dimensions = [length, width, height]
      .filter((dim) => dim)
      .join(' x ');

    return dimensions;
  }

  toFeetAndInches(totalInches: number): string {
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}-${inches}`;
  }

  toggleSavedLoad(load: any): void {
    const originalValue = load.is_saved;
    load.is_saved = !originalValue;

    this.appService
      .patch(`saved/loads/${load.id}/toggle-saved`, {})
      .subscribe({
        next: (res) => {
          load.is_saved = res.data.is_saved;
          this.sortFavoritesOnTop();
        },
        error: (err) => {
          load.is_saved = originalValue;
          this.appService.handleError(err);
        },
      });
  }

  private sortFavoritesOnTop(): void {
    this.results.data = [...this.results.data].sort((a, b) => {
      const savedCompare = Number(b.is_saved) - Number(a.is_saved);

      if (savedCompare !== 0) {
        return savedCompare;
      }

      return new Date(b.refreshed_at).getTime() - new Date(a.refreshed_at).getTime();
    });
  }

  formatAge(refreshedSince: string): string {
    if (!refreshedSince) {
      return '';
    }

    const [hours, minutes] = refreshedSince.split(':').map(Number);

    if (hours < 24) {
      return refreshedSince;
    }

    const days = Math.floor(hours / 24);

    return `${days} day${days > 1 ? 's' : ''}`;
  }
}
